import { createAsyncThunk } from '@reduxjs/toolkit';
import { Paginated, PaginationService } from '../../services/api-handlers/pagination';
import { Article, QueryParams } from '../../types';
import SortingService from '../../services/sorting';
import { ArticleApi } from '../../services/end-points';
import { resolveApiCall } from '../../services/api-handlers/api-resolver';
import { ArticleFormData } from '../../screens/Article/components/ArticleForm';
import { makeFormErrorsFromResponse } from '../../components/react-hook-form/utils/make-form-errors';
import { ThunkConfig } from '../configure-store';

export const listArticles = createAsyncThunk<Paginated<Article>, QueryParams, ThunkConfig>(
    'article/list',
    async (payload, thunkApi) => {
        const { getState } = thunkApi;
        const { article } = getState();
        const { filters, sorting, ...rest } = payload;

        return PaginationService.resolveApiCall(
            thunkApi,
            article,
            async () =>
                ArticleApi.list({
                    ...filters,
                    ...SortingService.makeOrder(sorting),
                    ...rest,
                }),
            payload.page,
            article.data,
        );
    },
);

export const getAllArticles = createAsyncThunk<Paginated<Article>, undefined, ThunkConfig>(
    'article/all-list',
    async (payload, thunkApi) => {
        const { getState } = thunkApi;
        const { article } = getState();

        return PaginationService.resolveApiCall(
            thunkApi,
            article,
            async () =>
                ArticleApi.list({
                    limit: -1,
                }),
            1,
            {
                list: [],
                page: 1,
            },
        );
    },
);

export const getArticle = createAsyncThunk<Article, { id: string; onError: (data: any) => any }, ThunkConfig>(
    'article/current',
    async (payload, thunkApi) => {
        const { getState } = thunkApi;
        const { article } = getState();
        const { id, onError } = payload;

        return resolveApiCall(thunkApi, article, async () => (await ArticleApi.get(id)).data, onError);
    },
);

export const deleteArticle = createAsyncThunk<any, { id: string; callback: () => void }, ThunkConfig>(
    'article/delete',
    async (payload, thunkApi) => {
        const { getState } = thunkApi;
        const { id } = payload;
        const { article } = getState();
        await resolveApiCall(
            thunkApi,
            article,
            async () => (
                await ArticleApi.remove(id),
                async (err: { response: any }) => {
                    const { response } = err;
                    return makeFormErrorsFromResponse(response.data);
                }
            ),
        );
        payload.callback();
    },
);

export const createArticle = createAsyncThunk<Article, ArticleFormData, ThunkConfig>(
    'article/create',
    async (payload, thunkApi) => {
        const { getState } = thunkApi;
        const { article } = getState();
        return resolveApiCall(
            thunkApi,
            article,
            async () => await ArticleApi.create(payload),
            async (err) => {
                const { response } = err;
                return makeFormErrorsFromResponse(response.data);
            },
        );
    },
);

export const updateArticle = createAsyncThunk<
    Article,
    { id: Article['id']; data: Partial<ArticleFormData> },
    ThunkConfig
>('article/update', async (payload, thunkApi) => {
    const { getState } = thunkApi;
    const { article } = getState();
    const { id, data } = payload;
    return resolveApiCall(
        thunkApi,
        article,
        async () => await ArticleApi.update(id, data),
        async (err) => {
            const { response } = err;
            return makeFormErrorsFromResponse(response.data);
        },
    );
});
